import React from "react";

import type { ContextNode } from "../../types/contextGraph";

export interface ContextGraphScopeControlsProps {
	/** Every company in the answer, in label order. */
	companies: readonly ContextNode[];
	/** The company currently narrowed to, or `null` for the whole answer. */
	focus: string | null;
	onFocusChange: (focus: string | null) => void;
	expandAll: boolean;
	onExpandAllChange: (expandAll: boolean) => void;
}

const SELECT_CLASSES =
	"rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-text-base " +
	"focus:border-[#008782] focus:ring-[#008782]";

/** The value the `<select>` uses for "don't narrow to any one company". */
const WHOLE_ANSWER = "";

/**
 * How much of an answer to draw.
 *
 * A portfolio answer touches every company in the room, and all of it at once
 * is a thread nobody can read. The reader picks one company's neighbourhood —
 * which is the shape the screen was designed around — or asks for the whole
 * answer and accepts the density.
 *
 * Rendered only when there is a choice to make: one company is not a menu.
 */
const ContextGraphScopeControls: React.FC<ContextGraphScopeControlsProps> = ({
	companies,
	focus,
	onFocusChange,
	expandAll,
	onExpandAllChange,
}) => (
	<div className="flex shrink-0 items-center gap-2">
		{companies.length > 1 && (
			<label className="text-sm text-text-muted">
				<span className="sr-only">Company</span>
				<select
					className={SELECT_CLASSES}
					value={focus ?? WHOLE_ANSWER}
					onChange={(event) =>
						onFocusChange(event.target.value === WHOLE_ANSWER ? null : event.target.value)
					}
				>
					<option value={WHOLE_ANSWER}>Whole answer</option>
					{companies.map((company) => (
						<option key={company.id} value={company.id}>
							{company.label}
						</option>
					))}
				</select>
			</label>
		)}

		<button
			type="button"
			onClick={() => onExpandAllChange(!expandAll)}
			aria-pressed={expandAll}
			className={`rounded-lg border px-3 py-1.5 text-sm ${
				expandAll
					? "border-primary bg-primary-50 text-primary"
					: "border-gray-300 text-text-base hover:bg-gray-50"
			}`}
		>
			{expandAll ? "Collapse" : "Expand all"}
		</button>
	</div>
);

export default ContextGraphScopeControls;
